// this is the admin ticket page not the dashboard page



import { useEffect, useState, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAdminDashboard } from '../api/dashboard';
import { getTickets, updateTicket } from '../api/tickets';
import { toast } from 'sonner';
import { Plus, Search, Filter, Calendar, Star, MoreVertical, Download, X, User, Tag as TagIcon, CheckCircle2, XCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Button from '../components/ui/button';
import Input from '../components/ui/input';
import DashboardLayout from '../components/DashboardLayout';
import api from '../api/axios';

/** Backend getTickets caps limit at 100. */
const TICKETS_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const AllTickets = () => {
  const { user } = useAuth();
  const normalizedRole = String(user?.role || '').toLowerCase();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dashboardData, setDashboardData] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    category_id: '',
    dateFrom: '',
    dateTo: '',
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  // Prepare employee options for SearchableSelect
  const employeeOptions = useMemo(() => 
    employees.map((emp) => ({
      value: String(emp.id),
      label: `${emp.name}${emp.department_name ? ` (${emp.department_name})` : ''}`.trim(),
    })), [employees]
  );
  const getEmployeeIdByLabel = (label = '') => {
    const q = String(label || '').trim().toLowerCase();
    if (!q) return null;
    const exact = employeeOptions.find((opt) => opt.label.toLowerCase() === q);
    if (exact) return exact.value;
    const starts = employeeOptions.filter((opt) => opt.label.toLowerCase().startsWith(q));
    return starts.length === 1 ? starts[0].value : null;
  };
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [favoriteTickets, setFavoriteTickets] = useState([]);
  const [ticketListPage, setTicketListPage] = useState(1);
  const [ticketsPageSize, setTicketsPageSize] = useState(20);
  const [ticketListPagination, setTicketListPagination] = useState(null);
  const ticketListFiltersSigRef = useRef('');
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showBulkPriorityModal, setShowBulkPriorityModal] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState('');
  const [bulkPriority, setBulkPriority] = useState('');
  const [activeFilter, setActiveFilter] = useState(() => {
    // Get filter from URL params, default to 'all'
    return searchParams.get('filter') || 'all';
  });

  useEffect(() => {
    fetchDashboardData();
    fetchEmployees();
    fetchCategories();
    loadFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Sync URL params with activeFilter
  useEffect(() => {
    const filterFromUrl = searchParams.get('filter') || 'all';
    if (filterFromUrl !== activeFilter) {
      setActiveFilter(filterFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const sig = JSON.stringify({ activeFilter, searchTerm, advancedFilters, ticketsPageSize });
    const isFirstRun = ticketListFiltersSigRef.current === '';
    const filtersChanged = !isFirstRun && ticketListFiltersSigRef.current !== sig;
    ticketListFiltersSigRef.current = sig;

    if (filtersChanged && ticketListPage !== 1) {
      setTicketListPage(1);
      return;
    }

    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilter, searchTerm, advancedFilters, ticketListPage, ticketsPageSize]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  // Update URL when filter changes
  // eslint-disable-next-line no-unused-vars
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ filter });
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await getAdminDashboard();
      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const tid = user?.tenant_id || 1;
      const response = await api.get(`/users?role=employee,admin&assignment_tenant_id=${tid}`);
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      if (response.data.success) {
        setCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const loadFavorites = () => {
    if (user?.id) {
      const favoriteKey = `favorite_tickets_${user.id}`;
      const favorites = JSON.parse(localStorage.getItem(favoriteKey) || '[]');
      // Normalize all IDs to numbers for consistent comparison
      const normalizedFavorites = favorites.map(id => typeof id === 'string' ? parseInt(id, 10) : id);
      setFavoriteTickets(normalizedFavorites);
    }
  };

  const toggleFavorite = (ticketId) => {
    if (!user?.id) return;
    
    // Ensure ticketId is a number for consistent comparison
    const ticketIdNum = typeof ticketId === 'string' ? parseInt(ticketId, 10) : ticketId;
    
    const favoriteKey = `favorite_tickets_${user.id}`;
    const favorites = JSON.parse(localStorage.getItem(favoriteKey) || '[]');
    
    // Normalize all IDs to numbers for consistent comparison
    const normalizedFavorites = favorites.map(id => typeof id === 'string' ? parseInt(id, 10) : id);
    
    let newFavorites;
    if (normalizedFavorites.includes(ticketIdNum)) {
      newFavorites = normalizedFavorites.filter(id => id !== ticketIdNum);
      toast.success('Removed from favorites');
    } else {
      newFavorites = [...normalizedFavorites, ticketIdNum];
      toast.success('Added to favorites');
    }
    
    localStorage.setItem(favoriteKey, JSON.stringify(newFavorites));
    setFavoriteTickets(newFavorites);
    
    // If we're on the favorites filter, refresh the tickets
    if (activeFilter === 'favorites') {
      fetchTickets();
    }
  };

  const isFavorite = (ticketId) => {
    // Ensure ticketId is a number for consistent comparison
    const ticketIdNum = typeof ticketId === 'string' ? parseInt(ticketId, 10) : ticketId;
    // Normalize favorites array
    const normalizedFavorites = favoriteTickets.map(id => typeof id === 'string' ? parseInt(id, 10) : id);
    return normalizedFavorites.includes(ticketIdNum);
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      // Clear any previous selection when loading new tickets
      setSelectedTicketIds([]);
      const params = { ...advancedFilters };
      
      // Apply active filter
      switch (activeFilter) {
        case 'my':
          params.assigned_to = user?.id;
          break;
        case 'unassigned':
          params.assigned_to = '';
          params.unassigned = true;
          break;
        case 'pending':
          // Pending = open + in_progress
          params.status = 'open,in_progress';
          break;
        case 'resolved':
          // Resolved view should include both RESOLVED and CLOSED tickets.
          params.status = 'resolved,closed';
          break;
        case 'closed':
          params.status = 'closed';
          break;
        case 'favorites':
          // Favorites will be filtered client-side
          break;
        case 'all':
        default:
          // No additional filters
          break;
      }
      
      if (params.assigned_to === 'unassigned') {
        params.assigned_to = '';
        params.unassigned = true;
      }
      
      // "Pending" (from the advanced-filter dropdown) isn't a real DB status; translate it
      if (params.status === 'pending') params.status = 'open,in_progress';
      
      // Apply date range filter
      if (params.dateFrom) {
        params.date_from = params.dateFrom;
        delete params.dateFrom;
      }
      if (params.dateTo) {
        params.date_to = params.dateTo;
        delete params.dateTo;
      }

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === false) delete params[key];
      });

      params.page = ticketListPage;
      params.limit = ticketsPageSize;

      const response = await getTickets(params);
      if (response.success) {
        const pagination = response.data.pagination;
        setTicketListPagination(pagination || null);
        let filteredTickets = response.data.tickets;
        
        // Reload favorites to ensure they're in sync
        loadFavorites();
        
        // Apply favorites filter (client-side)
        if (activeFilter === 'favorites') {
          const favoriteKey = `favorite_tickets_${user?.id}`;
          const favorites = JSON.parse(localStorage.getItem(favoriteKey) || '[]');
          // Normalize all IDs to numbers for consistent comparison
          const normalizedFavorites = favorites.map(id => typeof id === 'string' ? parseInt(id, 10) : id);
          filteredTickets = filteredTickets.filter(ticket => {
            const ticketIdNum = typeof ticket.id === 'string' ? parseInt(ticket.id, 10) : ticket.id;
            return normalizedFavorites.includes(ticketIdNum);
          });
        }
        
        // Resolved metric filter should include both resolved and closed.
        // Keep this as a frontend fallback so behavior is correct even if backend status parsing
        // is not yet restarted/deployed.
        const showResolvedOnly = activeFilter === 'resolved';
        if (showResolvedOnly) {
          filteredTickets = filteredTickets.filter((t) => {
            const s = String(t.status || '').toLowerCase();
            return s === 'resolved' || s === 'closed';
          });
        }
        
        // Apply date range filter on frontend if backend doesn't support it
        if (advancedFilters.dateFrom || advancedFilters.dateTo) {
          filteredTickets = filteredTickets.filter(ticket => {
            const ticketDate = new Date(ticket.created_at);
            if (advancedFilters.dateFrom && ticketDate < new Date(advancedFilters.dateFrom)) return false;
            if (advancedFilters.dateTo) {
              const toDate = new Date(advancedFilters.dateTo);
              toDate.setHours(23, 59, 59, 999);
              if (ticketDate > toDate) return false;
            }
            return true;
          });
        }

        if (searchTerm) {
          filteredTickets = filteredTickets.filter(
            (ticket) =>
              ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
              ticket.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
              ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
              ticket.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              ticket.customer_company?.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        
        setTickets(filteredTickets);
      }
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAssign = async (ticketId, employeeId) => {
    try {
      const response = await updateTicket(ticketId, {
        assigned_to: employeeId,
        status: employeeId ? 'in_progress' : 'open',
      });
      if (response.success) {
        toast.success('Ticket assigned successfully');
        fetchTickets();
        fetchDashboardData();
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to assign ticket'));
    }
  };

  // --- Helper Components for the Table ---
  const PriorityBadge = ({ priority }) => {
    const styles = {
      urgent: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20 shadow-red-500/10',
      high: 'bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20 shadow-orange-500/10',
      medium: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/20 shadow-yellow-500/10',
      low: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20 shadow-blue-500/10',
    };
    const defaultStyle = styles.low;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border shadow-sm ${styles[priority?.toLowerCase()] || defaultStyle}`}>
        {priority?.toUpperCase() || 'LOW'}
      </span>
    );
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      new: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/20',
      open: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20',
      in_progress: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
      resolved: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
      closed: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700',
      cancelled: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20',
    };
    const defaultStyle = styles.open;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status?.toLowerCase()] || defaultStyle}`}>
        <span className="relative flex h-1.5 w-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-current"></span>
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-current"></span>
        </span>
        {status?.replace('_', ' ').toUpperCase() || 'OPEN'}
      </span>
    );
  };

  const isTicketSelected = (id) => selectedTicketIds.includes(id);
  const areAllSelected = tickets.length > 0 && selectedTicketIds.length === tickets.length;

  const toggleSelectAll = () => {
    if (areAllSelected) {
      setSelectedTicketIds([]);
    } else {
      setSelectedTicketIds(tickets.map((t) => t.id));
    }
  };

  const toggleTicketSelection = (id) => {
    setSelectedTicketIds((prev) =>
      prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id]
    );
  };

  const clearSelection = () => setSelectedTicketIds([]);

  const handleBulkAssignToMe = async () => {
    if (!user?.id || selectedTicketIds.length === 0) return;
    try {
      setBulkUpdating(true);
      await Promise.all(
        selectedTicketIds.map((id) =>
          updateTicket(id, {
            assigned_to: user.id,
            status: 'in_progress',
          })
        )
      );
      toast.success(`Assigned ${selectedTicketIds.length} ticket(s) to you`);
      clearSelection();
      fetchTickets();
      fetchDashboardData();
    } catch (error) {
      console.error('Bulk assign error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to assign selected tickets'));
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkStatusChange = async (status) => {
    if (selectedTicketIds.length === 0) return;
    try {
      setBulkUpdating(true);
      await Promise.all(
        selectedTicketIds.map((id) =>
          updateTicket(id, {
            status,
          })
        )
      );
      const label = status.replace('_', ' ');
      toast.success(`Updated ${selectedTicketIds.length} ticket(s) to ${label}`);
      clearSelection();
      fetchTickets();
      fetchDashboardData();
    } catch (error) {
      console.error('Bulk status change error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update selected tickets'));
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignTo || selectedTicketIds.length === 0) return;
    try {
      setBulkUpdating(true);
      await Promise.all(
        selectedTicketIds.map((id) =>
          updateTicket(id, {
            assigned_to: bulkAssignTo,
            status: 'in_progress',
          })
        )
      );
      const assignee = employees.find(e => e.id.toString() === bulkAssignTo);
      toast.success(`Assigned ${selectedTicketIds.length} ticket(s) to ${assignee?.name || 'user'}`);
      setShowBulkAssignModal(false);
      setBulkAssignTo('');
      clearSelection();
      fetchTickets();
      fetchDashboardData();
    } catch (error) {
      console.error('Bulk assign error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to assign selected tickets'));
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleBulkPriorityChange = async () => {
    if (!bulkPriority || selectedTicketIds.length === 0) return;
    try {
      setBulkUpdating(true);
      await Promise.all(
        selectedTicketIds.map((id) =>
          updateTicket(id, {
            priority: bulkPriority,
          })
        )
      );
      toast.success(`Updated priority for ${selectedTicketIds.length} ticket(s) to ${bulkPriority}`);
      setShowBulkPriorityModal(false);
      setBulkPriority('');
      clearSelection();
      fetchTickets();
      fetchDashboardData();
    } catch (error) {
      console.error('Bulk priority change error:', error);
      toast.error(getApiErrorMessage(error, 'Failed to update priority'));
    } finally {
      setBulkUpdating(false);
    }
  };

  const exportToCSV = () => {
    if (tickets.length === 0) {
      toast.error('No tickets to export');
      return;
    }

    try {
      const headers = ['Ticket Number', 'Subject', 'Status', 'Priority', 'Category', 'Assigned To', 'Customer', 'Company', 'Created At', 'Updated At', 'Closed At'];
      const rows = tickets.map(ticket => [
        ticket.ticket_number || '',
        (ticket.subject || '').replace(/"/g, '""'),
        ticket.status || '',
        ticket.priority || '',
        (ticket.category_name || '').replace(/"/g, '""'),
        ((ticket.assigned_to_name || 'Unassigned').replace(/0+$/, '').trim()).replace(/"/g, '""'),
        (ticket.customer_name || '').replace(/"/g, '""'),
        (ticket.customer_company || 'N/A').replace(/"/g, '""'),
        new Date(ticket.created_at).toLocaleString(),
        new Date(ticket.updated_at).toLocaleString(),
        ticket.closed_at ? new Date(ticket.closed_at).toLocaleString() : '',
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""').replace(/\n/g, ' ').replace(/\r/g, '')}"`).join(','))
      ].join('\n');

      // Add BOM for UTF-8 to ensure Excel opens it correctly
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `tickets_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${tickets.length} ticket(s) to CSV`);
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    }
  };

  const exportToPDF = () => {
    if (tickets.length === 0) {
      toast.error('No tickets to export');
      return;
    }

    // Create HTML content for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Tickets Export</title>
          <style>
            @media print {
              @page {
                margin: 1cm;
                size: A4 landscape;
              }
            }
            body {
              font-family: Arial, sans-serif;
              font-size: 10px;
              margin: 0;
              padding: 20px;
            }
            h1 {
              text-align: center;
              margin-bottom: 20px;
              font-size: 18px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 10px;
            }
            th, td {
              border: 1px solid #000;
              padding: 6px;
              text-align: left;
            }
            th {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .header-info {
              margin-bottom: 20px;
              font-size: 12px;
            }
          </style>
        </head>
        <body>
          <h1>Ticket Export Report</h1>
          <div class="header-info">
            <p><strong>Export Date:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Total Tickets:</strong> ${tickets.length}</p>
          </div>
          <table>
            <thead>
              <tr>
                <th>Ticket Number</th>
                <th>Subject</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Category</th>
                <th>Assigned To</th>
                <th>Customer</th>
                <th>Created At</th>
                <th>Updated At</th>
                <th>Closed At</th>
              </tr>
            </thead>
            <tbody>
              ${tickets.map(ticket => `
                <tr>
                  <td>${ticket.ticket_number || 'N/A'}</td>
                  <td>${(ticket.subject || '').substring(0, 50)}</td>
                  <td>${ticket.status || 'N/A'}</td>
                  <td>${ticket.priority || 'N/A'}</td>
                  <td>${ticket.category_name || 'N/A'}</td>
                  <td>${(ticket.assigned_to_name || 'Unassigned').replace(/0+$/, '').trim()}</td>
                  <td>${ticket.customer_name || 'N/A'}</td>
                  <td>${new Date(ticket.created_at).toLocaleString()}</td>
                  <td>${new Date(ticket.updated_at).toLocaleString()}</td>
                  <td>${ticket.closed_at ? new Date(ticket.closed_at).toLocaleString() : 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    // Create a new window and write the HTML
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait for content to load, then trigger print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          toast.success('PDF export ready. Use your browser\'s print dialog to save as PDF.');
        }, 250);
      };
    } else {
      toast.error('Please allow pop-ups to export PDF');
    }
  };

  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      status: '',
      priority: '',
      assigned_to: '',
      category_id: '',
      dateFrom: '',
      dateTo: '',
    });
    // Keep panel open so user can set new filters without re-opening
  };

  return (
    <DashboardLayout userRole={user?.role || 'admin'}>
      <div className="min-h-full soft-mesh-gradient">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 glass-card px-3 md:px-6 py-3 md:py-4 relative z-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div>
                <h2 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-gray-100">Tickets</h2>
                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {dashboardData?.total || 0} {dashboardData?.total === 1 ? 'Ticket' : 'Tickets'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {/* Export CSV - Secondary Action */}
                <Button
                  variant="unstyled"
                  onClick={exportToCSV}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-9 px-3 text-xs bg-gradient-to-r from-emerald-500 via-teal-500 to-green-500 text-white rounded-lg shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 hover:scale-[1.04] active:scale-95 transition-all duration-300 touch-manipulation font-semibold"
                  title="Export to CSV"
                >
                  <Download className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden sm:inline leading-none">CSV</span>
                </Button>

                {/* Export PDF - Destructive/Red Variant (Common for PDF) */}
                <Button
                  variant="unstyled" 
                  onClick={exportToPDF}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-9 px-3 text-xs bg-gradient-to-r from-rose-600 via-red-500 to-pink-500 text-white rounded-lg shadow-lg shadow-rose-500/30 hover:shadow-rose-500/50 hover:-translate-y-0.5 hover:scale-[1.04] active:scale-95 transition-all duration-300 touch-manipulation font-semibold"
                  title="Export to PDF"
                >
                  <Download className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden sm:inline leading-none">PDF</span>
                </Button>

                {/* Create Ticket - Primary Action (Highest Priority) */}
                <Button
                  variant="unstyled"
                  onClick={() => navigate('/tickets/create')}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 px-4 md:px-6 text-sm font-semibold bg-gradient-to-r from-indigo-600 via-blue-500 to-cyan-500 text-white rounded-xl shadow-xl shadow-indigo-500/40 hover:shadow-indigo-500/60 hover:-translate-y-1 hover:scale-[1.05] active:scale-95 transition-all duration-300 touch-manipulation"
                >
                  <Plus className="w-3.5 h-3.5 shrink-0" />
                  <span className="hidden xs:inline leading-none">Create</span>
                  <span className="xs:hidden leading-none">New</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Search and Quick Filters - mobile-friendly */}
          <div className="mt-3 md:mt-4 mx-3 md:mx-0 border border-gray-200 dark:border-gray-700 rounded-xl md:rounded-none md:border-x-0 md:border-t-0 bg-white/80 dark:bg-slate-800/80 md:glass-card px-3 md:px-6 py-3 md:py-4 relative z-10">
            <div className="flex flex-col gap-3 md:gap-4">
              {/* Search - full width on mobile */}
              <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-3">
                <div className="flex-1 relative min-w-0 w-full">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-gray-400 dark:text-gray-500 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Search tickets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full min-w-0 pl-9 md:pl-10 pr-3 py-2.5 md:py-2.5 h-11 md:min-h-[44px] bg-white dark:bg-slate-700 border border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 rounded-xl md:rounded-lg transition-all duration-300 shadow-sm text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400"
                  />
                </div>

                {/* Status / Priority + More - horizontal scroll on mobile */}
                <div className="flex items-center gap-2 md:gap-3 overflow-x-auto overflow-y-hidden pb-1 md:pb-0 -mx-1 px-1 md:mx-0 md:px-0 scrollbar-thin min-w-0">
                  {/* Status Filter */}
                  <div className="relative flex-shrink-0">
                    <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-600 dark:text-blue-400 pointer-events-none z-10" />
                    <select
                      value={advancedFilters.status}
                      onChange={(e) => setAdvancedFilters({ ...advancedFilters, status: e.target.value })}
                      className="text-sm pl-9 pr-3 py-2.5 h-11 md:min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-xl md:rounded-lg bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none shadow-sm transition-all touch-manipulation min-w-[120px]"
                    >
                      <option value="">All Status</option>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="pending">Pending</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  {/* Priority Filter */}
                  <div className="relative flex-shrink-0">
                    <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-600 dark:text-red-400 pointer-events-none z-10" />
                    <select
                      value={advancedFilters.priority}
                      onChange={(e) => setAdvancedFilters({ ...advancedFilters, priority: e.target.value })}
                      className="text-sm pl-9 pr-3 py-2.5 h-11 md:min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-xl md:rounded-lg bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none shadow-sm transition-all touch-manipulation min-w-[120px]"
                    >
                      <option value="">All Priority</option>
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>

                  {/* More Filters Button */}
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    className={`flex-shrink-0 px-3 md:px-4 py-2.5 h-11 md:min-h-[44px] text-sm border border-gray-300 dark:border-gray-600 rounded-xl md:rounded-lg flex items-center gap-2 font-medium transition-all shadow-sm touch-manipulation ${
                      showAdvancedFilters 
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 dark:border-blue-400 text-blue-700 dark:text-blue-300' 
                        : 'bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    <Filter className={`w-4 h-4 flex-shrink-0 ${showAdvancedFilters ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                    <span className="whitespace-nowrap">More</span>
                    {(advancedFilters.assigned_to || advancedFilters.category_id || advancedFilters.dateFrom || advancedFilters.dateTo) && (
                      <span className="ml-1 px-2 py-0.5 bg-blue-600 dark:bg-blue-500 text-white text-xs font-bold rounded-full min-w-[20px] text-center flex-shrink-0">
                        {[advancedFilters.assigned_to, advancedFilters.category_id, advancedFilters.dateFrom, advancedFilters.dateTo].filter(Boolean).length}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            {/* Advanced Filters Panel - visually part of same card */}
            {showAdvancedFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 -mx-3 md:-mx-6 px-3 md:px-6 rounded-b-xl bg-gray-50/50 dark:bg-slate-800/50">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Filter className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    Advanced Filters
                  </h4>
                  <button
                    type="button"
                    onClick={clearAdvancedFilters}
                    className="px-3 py-2 text-xs font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 hover:border-gray-400 dark:hover:border-gray-500 flex items-center gap-2 transition-all text-gray-700 dark:text-gray-200 touch-manipulation self-start sm:self-auto min-h-[44px]"
                  >
                    <X className="w-3.5 h-3.5 flex-shrink-0" />
                    Clear All
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {/* Status */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Status
                    </label>
                    <div className="relative">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-600 dark:text-blue-400 pointer-events-none z-10" />
                      <select
                        value={advancedFilters.status}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, status: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 cursor-pointer transition-all appearance-none shadow-sm"
                      >
                        <option value="">All Statuses</option>
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                  </div>

                  {/* Priority */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Priority
                    </label>
                    <div className="relative">
                      <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-red-600 dark:text-red-400 pointer-events-none z-10" />
                      <select
                        value={advancedFilters.priority}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, priority: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 cursor-pointer transition-all appearance-none shadow-sm"
                      >
                        <option value="">All Priorities</option>
                        <option value="urgent">Urgent</option>
                        <option value="high">High</option>
                        <option value="medium">Medium</option>
                        <option value="low">Low</option>
                      </select>
                    </div>
                  </div>

                  {/* Assigned To */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Assigned To
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-600 dark:text-green-400 pointer-events-none z-10" />
                      <select
                        value={advancedFilters.assigned_to}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, assigned_to: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 cursor-pointer transition-all appearance-none shadow-sm"
                      >
                        <option value="">All Assignees</option>
                        <option value="unassigned">Unassigned</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Category */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Category
                    </label>
                    <div className="relative">
                      <TagIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-600 dark:text-purple-400 pointer-events-none z-10" />
                      <select
                        value={advancedFilters.category_id}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, category_id: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 cursor-pointer transition-all appearance-none shadow-sm"
                      >
                        <option value="">All Categories</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Date From */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Date From
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-indigo-600 dark:text-indigo-400 pointer-events-none z-10" />
                      <input
                        type="date"
                        value={advancedFilters.dateFrom}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateFrom: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all shadow-sm"
                      />
                    </div>
                  </div>

                  {/* Date To */}
                  <div className="space-y-1.5">
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Date To
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-indigo-600 dark:text-indigo-400 pointer-events-none z-10" />
                      <input
                        type="date"
                        value={advancedFilters.dateTo}
                        onChange={(e) => setAdvancedFilters({ ...advancedFilters, dateTo: e.target.value })}
                        className="w-full pl-9 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-gray-700 dark:text-gray-300 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table + Mobile cards */}
          <div className="px-3 md:px-6 py-3 md:py-4 relative z-10">
            <div className="overflow-hidden">
              {/* Bulk actions bar - No changes needed here, keeping logic intact */}
              {selectedTicketIds.length > 0 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-0 md:px-0 py-3 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-800 text-sm text-blue-900 dark:text-blue-200 transition-colors duration-300">
                  <span className="font-medium flex items-center gap-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-200 dark:bg-blue-800 text-blue-700 dark:text-blue-100 text-xs font-bold">
                      {selectedTicketIds.length}
                    </span>
                    <span>selected</span>
                  </span>

                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Assign to Me */}
                    <button
                      type="button"
                      onClick={handleBulkAssignToMe}
                      disabled={bulkUpdating}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 bg-gradient-to-r from-indigo-500 via-indigo-500 to-blue-500 text-white rounded-lg tracking-wide shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none disabled:hover:translate-y-0 disabled:hover:scale-100 font-semibold"
                    >
                      <User className="w-3.5 h-3.5 shrink-0" />
                      <span className="hidden sm:inline leading-none">Assign to me</span>
                      <span className="sm:hidden leading-none">Me</span>
                    </button>

                    {/* Assign To... */}
                    <button
                      type="button"
                      onClick={() => setShowBulkAssignModal(true)}
                      disabled={bulkUpdating}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 text-white rounded-lg tracking-wide shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none disabled:hover:translate-y-0 disabled:hover:scale-100 font-semibold"
                    >
                      <User className="w-3.5 h-3.5 shrink-0" />
                      <span className="hidden sm:inline leading-none">Assign to</span>
                      <span className="sm:hidden leading-none">Assign</span>
                    </button>

                    {/* Priority */}
                    <button
                      type="button"
                      onClick={() => setShowBulkPriorityModal(true)}
                      disabled={bulkUpdating}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-400 text-white rounded-lg tracking-wide shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none disabled:hover:translate-y-0 disabled:hover:scale-100 font-semibold"
                    >
                      <TagIcon className="w-3.5 h-3.5 shrink-0" />
                      <span className="hidden sm:inline leading-none">Priority</span>
                      <span className="sm:hidden leading-none">Priority</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleBulkStatusChange('resolved')}
                      disabled={bulkUpdating}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-400 text-white rounded-lg tracking-wide shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none disabled:hover:translate-y-0 disabled:hover:scale-100 font-semibold"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                      <span className="hidden sm:inline leading-none">Resolved</span>
                      <span className="sm:hidden leading-none">Resolve</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleBulkStatusChange('closed')}
                      disabled={bulkUpdating}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 bg-gradient-to-r from-slate-700 via-slate-600 to-indigo-500 text-white rounded-lg tracking-wide shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:pointer-events-none disabled:hover:translate-y-0 disabled:hover:scale-100 font-semibold"
                    >
                      <XCircle className="w-3.5 h-3.5 shrink-0" />
                      <span className="hidden sm:inline leading-none">Closed</span>
                      <span className="sm:hidden leading-none">Close</span>
                    </button>

                    {/* Clear Selection */}
                    <button
                      type="button"
                      onClick={clearSelection}
                      disabled={bulkUpdating}
                      className="inline-flex items-center justify-center gap-2 whitespace-nowrap h-11 text-xs px-3 md:px-4 rounded-lg tracking-wide bg-gradient-to-r from-slate-100 via-slate-50 to-violet-100 text-slate-800 hover:text-slate-900 shadow-md shadow-violet-300/40 hover:shadow-violet-400/60 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-95 transition-all duration-300 dark:from-slate-700 dark:via-slate-800 dark:to-violet-700 dark:text-slate-100 dark:hover:text-white disabled:opacity-50 disabled:pointer-events-none disabled:hover:translate-y-0 disabled:hover:scale-100 font-semibold"
                    >
                      <span className="leading-none">Clear</span>
                    </button>
                  </div>
                </div>
              )}
              
              {/* Mobile: card list */}
              <div className="md:hidden space-y-3 pb-4">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="text-center py-12 px-4 bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">No tickets found</p>
                  </div>
                ) : (
                  tickets.map((ticket) => (
                    <div
                      key={ticket.id}
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                      className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-3.5 shadow-sm active:scale-[0.99] transition-transform cursor-pointer"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 pt-0.5" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/30"
                            checked={isTicketSelected(ticket.id)}
                            onChange={() => toggleTicketSelection(ticket.id)}
                            aria-label={`Select ticket ${ticket.ticket_number}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-xs font-medium text-indigo-600 dark:text-indigo-400">{ticket.ticket_number}</span>
                            <StatusBadge status={ticket.status} />
                            <PriorityBadge priority={ticket.priority} />
                          </div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white mt-1 line-clamp-2">{ticket.subject || 'No subject'}</p>
                          <div className="flex items-center justify-between mt-2 flex-wrap gap-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">{ticket.category_name || 'General'}</span>
                            {ticket.assigned_to_name ? (
                              <span className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[120px]">{(ticket.assigned_to_name || '').replace(/0+$/, '').trim()}</span>
                            ) : (
                              <div className="w-[160px]" onClick={e => e.stopPropagation()}>
                                <input
                                  list={`assign-options-mobile-${ticket.id}`}
                                  placeholder="Type to assign..."
                                  className="w-full text-xs border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 rounded-md py-1.5 px-2 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 cursor-text"
                                  onChange={(e) => {
                                    const assigneeId = getEmployeeIdByLabel(e.target.value);
                                    if (assigneeId) {
                                      handleQuickAssign(ticket.id, assigneeId);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                                <datalist id={`assign-options-mobile-${ticket.id}`}>
                                  {employeeOptions.map((opt) => (
                                    <option key={opt.value} value={opt.label} />
                                  ))}
                                </datalist>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); toggleFavorite(ticket.id); }}
                            className="text-gray-400 hover:text-yellow-500 p-1"
                            aria-label={isFavorite(ticket.id) ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            <Star className={`w-4 h-4 ${isFavorite(ticket.id) ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                          </button>
                          {(normalizedRole === 'admin') && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === ticket.id ? null : ticket.id); }}
                              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                              aria-label="More options"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {openMenuId === ticket.id && (normalizedRole === 'admin') && null}
                    </div>
                  ))
                )}
              </div>

              {/* Desktop: table */}
              <div className="hidden md:block overflow-visible rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl shadow-gray-200/50 dark:shadow-black/40">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse min-w-[1000px]">
                    <thead>
                      <tr className="bg-gray-50/50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-700 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <th className="p-4 w-[50px] font-medium text-center">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/30"
                            checked={areAllSelected}
                            onChange={toggleSelectAll}
                            aria-label="Select all tickets"
                          />
                        </th>
                        <th className="p-4 w-[100px] font-semibold whitespace-nowrap">Number</th>
                        <th className="p-4 w-[120px] font-semibold whitespace-nowrap">Opened</th>
                        <th className="p-4 w-[300px] font-semibold">Description</th> {/* Increased width for description */}
                        <th className="p-4 w-[110px] font-semibold whitespace-nowrap">Priority</th>
                        <th className="p-4 w-[130px] font-semibold whitespace-nowrap">State</th>
                        <th className="p-4 w-[140px] font-semibold whitespace-nowrap">Category</th>
                        <th className="p-4 w-[160px] font-semibold whitespace-nowrap">Assigned To</th>
                        {/* Removed 'hidden' classes from below columns */}
                        <th className="p-4 w-[140px] font-semibold whitespace-nowrap">Caller</th>
                        <th className="p-4 w-[120px] font-semibold whitespace-nowrap">Updated</th>
                        <th className="p-4 w-[120px] font-semibold whitespace-nowrap">Closed</th>
                        <th className="p-4 w-[50px]"></th>
                        <th className="p-4 w-[50px]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                      {loading ? (
                        <tr>
                          <td colSpan="13" className="p-8 text-center">
                            <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto"></div>
                          </td>
                        </tr>
                      ) : tickets.length === 0 ? (
                        <tr>
                          <td colSpan="13" className="p-8 text-center text-gray-500 dark:text-gray-400">
                            No tickets found
                          </td>
                        </tr>
                      ) : (
                        tickets.map((ticket) => (
                          <tr 
                            key={ticket.id}
                            onClick={() => navigate(`/tickets/${ticket.id}`)}
                            className="group bg-white dark:bg-slate-900 hover:bg-indigo-50/30 dark:hover:bg-indigo-900/10 transition-all duration-200 cursor-pointer hover:shadow-lg hover:shadow-indigo-500/5 hover:-translate-y-[1px]"
                          >
                            {/* Checkbox */}
                            <td className="p-4 text-center" onClick={e => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-500/30"
                                checked={isTicketSelected(ticket.id)}
                                onChange={() => toggleTicketSelection(ticket.id)}
                                aria-label={`Select ticket ${ticket.ticket_number}`}
                              />
                            </td>

                            {/* Number */}
                            <td className="p-4 font-mono text-xs font-medium text-indigo-600 dark:text-indigo-400 whitespace-nowrap">
                              {ticket.ticket_number || 'N/A'}
                            </td>

                            {/* Date */}
                            <td className="p-4 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              <div className="font-medium text-gray-700 dark:text-gray-300">
                                {new Date(ticket.created_at).toLocaleDateString(undefined, { month:'short', day:'numeric' })}
                              </div>
                              <div>{new Date(ticket.created_at).toLocaleTimeString(undefined, { hour:'2-digit', minute:'2-digit' })}</div>
                            </td>

                            {/* Description - Allowed to wrap */}
                            <td className="p-4">
                              <div className="font-medium text-sm text-gray-900 dark:text-white line-clamp-1 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" title={ticket.subject}>
                                {ticket.subject || 'No subject'}
                              </div>
                            </td>

                            {/* Priority Badge */}
                            <td className="p-4 whitespace-nowrap">
                              <PriorityBadge priority={ticket.priority} />
                            </td>

                            {/* Status Badge */}
                            <td className="p-4 whitespace-nowrap">
                              <StatusBadge status={ticket.status} />
                            </td>

                            {/* Category */}
                            <td className="p-4 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                              <span className="px-2 py-1 rounded bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300">
                                {ticket.category_name || 'General'}
                              </span>
                            </td>

                            {/* Assigned To */}
                            <td className="p-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                              {ticket.assigned_to_name ? (
                                <div className="flex items-center gap-2">
                                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-[10px] text-white font-bold shadow-sm">
                                    {(ticket.assigned_to_name || '').replace(/0+$/, '').trim().charAt(0).toUpperCase()}
                                  </div>
                                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[100px]">
                                    {(ticket.assigned_to_name || '').replace(/0+$/, '').trim()}
                                  </span>
                                </div>
                              ) : (
                                <div className="w-[160px]">
                                  <input
                                    list={`assign-options-desktop-${ticket.id}`}
                                    placeholder="Type to assign..."
                                    className="w-full text-xs border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 rounded-md py-1.5 px-2 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 cursor-text"
                                    onChange={(e) => {
                                      const assigneeId = getEmployeeIdByLabel(e.target.value);
                                      if (assigneeId) {
                                        handleQuickAssign(ticket.id, assigneeId);
                                        e.target.value = '';
                                      }
                                    }}
                                  />
                                  <datalist id={`assign-options-desktop-${ticket.id}`}>
                                    {employeeOptions.map((opt) => (
                                      <option key={opt.value} value={opt.label} />
                                    ))}
                                  </datalist>
                                </div>
                              )}
                            </td>

                            {/* Caller - Removed hidden class */}
                            <td className="p-4 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap" title={ticket.customer_name || 'N/A'}>
                              {ticket.customer_name || 'N/A'}
                            </td>

                            {/* Updated - Removed hidden class */}
                            <td className="p-4 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                              {new Date(ticket.updated_at).toLocaleDateString()}
                            </td>

                            {/* Closed Date - Removed hidden class */}
                            <td className="p-4 text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                              {ticket.closed_at ? (
                                <div>
                                  <div className="font-medium text-gray-700 dark:text-gray-300">
                                    {new Date(ticket.closed_at).toLocaleDateString(undefined, { month:'short', day:'numeric' })}
                                  </div>
                                  <div>{new Date(ticket.closed_at).toLocaleTimeString(undefined, { hour:'2-digit', minute:'2-digit' })}</div>
                                </div>
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600">-</span>
                              )}
                            </td>

                            {/* Actions: Favorite */}
                            <td className="p-4 text-center whitespace-nowrap">
                              <button 
                                onClick={(e) => { e.stopPropagation(); toggleFavorite(ticket.id); }}
                                className="text-gray-400 hover:text-yellow-500 hover:scale-110 transition-transform"
                                title={isFavorite(ticket.id) ? 'Remove from favorites' : 'Add to favorites'}
                                aria-label={isFavorite(ticket.id) ? 'Remove from favorites' : 'Add to favorites'}
                              >
                                <Star className={`w-4 h-4 ${isFavorite(ticket.id) ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                              </button>
                            </td>

                            {/* Actions: Menu */}
                            <td className="p-4 text-center relative whitespace-nowrap">
                              {(normalizedRole === 'admin') && (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === ticket.id ? null : ticket.id); }}
                                    className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                                    title="More options"
                                    aria-label="More options"
                                    aria-expanded={openMenuId === ticket.id}
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </button>
                                  {openMenuId === ticket.id && null}
                                </>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {(() => {
                const clientSideListMode =
                  !!searchTerm.trim() ||
                  !!advancedFilters.dateFrom ||
                  !!advancedFilters.dateTo ||
                  activeFilter === 'favorites';
                if (loading || !ticketListPagination || clientSideListMode) return null;
                const { page, limit, total, totalPages } = ticketListPagination;
                const totalNum = Number(total) || 0;
                const pageNum = Number(page) || 1;
                const limitNum = Number(limit) || ticketsPageSize;
                const start = totalNum === 0 ? 0 : (pageNum - 1) * limitNum + 1;
                const end = Math.min(pageNum * limitNum, totalNum);
                const pagesNum = Number(totalPages) || 1;
                return (
                  <div className="mt-4 flex flex-nowrap items-center gap-2 sm:gap-3 w-full min-w-0 overflow-x-auto border-t border-gray-200 dark:border-slate-700 pt-3 pb-1 scrollbar-thin">
                    <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap shrink-0">
                      {`Showing ${start}–${end} of ${totalNum} tickets`}
                    </p>
                    <label className="flex items-center gap-1.5 sm:gap-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap shrink-0">
                      <span className="whitespace-nowrap">Rows</span>
                      <select
                        value={ticketsPageSize}
                        onChange={(e) => setTicketsPageSize(Number(e.target.value))}
                        className="h-9 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 pl-2 pr-7 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                        aria-label="Rows per page"
                      >
                        {TICKETS_PAGE_SIZE_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    {pagesNum > 1 && (
                      <div className="flex flex-nowrap items-center gap-1 shrink-0 ml-auto">
                        <button
                          type="button"
                          disabled={pageNum <= 1}
                          onClick={() => setTicketListPage(1)}
                          className="inline-flex items-center gap-0.5 px-2 py-1.5 text-xs sm:text-sm font-medium rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                          aria-label="First page"
                        >
                          <ChevronsLeft className="w-4 h-4 shrink-0" />
                          <span className="hidden sm:inline">First</span>
                        </button>
                        <button
                          type="button"
                          disabled={pageNum <= 1}
                          onClick={() => setTicketListPage((p) => Math.max(1, p - 1))}
                          className="inline-flex items-center gap-0.5 px-2 py-1.5 text-xs sm:text-sm font-medium rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                          aria-label="Previous page"
                        >
                          <ChevronLeft className="w-4 h-4 shrink-0" />
                          <span className="hidden sm:inline">Previous</span>
                        </button>
                        <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 tabular-nums px-1.5 sm:px-2 whitespace-nowrap">
                          Page {pageNum} / {pagesNum}
                        </span>
                        <button
                          type="button"
                          disabled={pageNum >= pagesNum}
                          onClick={() => setTicketListPage((p) => p + 1)}
                          className="inline-flex items-center gap-0.5 px-2 py-1.5 text-xs sm:text-sm font-medium rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                          aria-label="Next page"
                        >
                          <span className="hidden sm:inline">Next</span>
                          <ChevronRight className="w-4 h-4 shrink-0" />
                        </button>
                        <button
                          type="button"
                          disabled={pageNum >= pagesNum}
                          onClick={() => setTicketListPage(pagesNum)}
                          className="inline-flex items-center gap-0.5 px-2 py-1.5 text-xs sm:text-sm font-medium rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                          aria-label="Last page"
                        >
                          <span className="hidden sm:inline">Last</span>
                          <ChevronsRight className="w-4 h-4 shrink-0" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
      </div>

      {/* Bulk Assign Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 md:p-6 w-full max-w-sm border border-gray-300 dark:border-gray-600 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Assign Tickets</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Assign to:</label>
              <select
                value={bulkAssignTo}
                onChange={(e) => setBulkAssignTo(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">Select user...</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowBulkAssignModal(false);
                  setBulkAssignTo('');
                }}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkAssign}
                disabled={!bulkAssignTo || bulkUpdating}
                className="px-4 py-2 text-sm btn-submit-gradient disabled:opacity-50"
              >
                Assign
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Priority Modal */}
      {showBulkPriorityModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-4 md:p-6 w-full max-w-sm border border-gray-300 dark:border-gray-600 shadow-xl">
            <h3 className="text-lg font-semibold mb-4">Change Priority</h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Priority:</label>
              <select
                value={bulkPriority}
                onChange={(e) => setBulkPriority(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="">Select priority...</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowBulkPriorityModal(false);
                  setBulkPriority('');
                }}
                className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkPriorityChange}
                disabled={!bulkPriority || bulkUpdating}
                className="px-4 py-2 text-sm btn-submit-gradient disabled:opacity-50"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

    </DashboardLayout>
  );
};

export default AllTickets;
